// @ts-strict-ignore
import {
  AfterViewChecked,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  OnInit, SimpleChanges, OnChanges,
} from '@angular/core';
import { SohoTabsComponent } from 'ids-enterprise-ng';
import { LMDetailSectionBase } from './lm-detail-section-base';
import { LandmarkPanel } from '@form/lm-form.model';
import { LMBusyIndicatorService } from '@services/lm-busyindicator.service';
import { LMWorker } from '../../types/lm-worker';
import {
  LMContextService,
  LMErrorUtilService,
  LMLoggerService,
  LMOverlayService,
  LMUrlUtilService
} from '@services/index';
import { LMCubeDetailSectionPVPeriodEvent, LMListComponent, LMListTransactionEvent } from './lm-list.component';
import { Observable, of } from 'rxjs';
import { LMDetailSectionDashBoardComponent } from './lm-detail-section-dashboard.component';
import { AppConstants } from '@utils/app.constants';
import { LMDetailSectionListComponent, LMFormRequestService, LMRefreshRequest } from '@app/components';
import urltemplate from 'url-template';
import { LMConditionUtilService } from '@app/utils';
import { first } from 'rxjs/operators';

export class LMDetailSectionsSaveWorker extends LMWorker {
  readonly detailSections: LMDetailSectionBase[];

  constructor(detailSections: LMDetailSectionBase[]) {
    super();

    this.detailSections = detailSections;
  }

  protected has(i: number): boolean {
    return (i < this.detailSections.length);
  }

  protected get(i: number): LMDetailSectionBase {
    return this.detailSections[ i ];
  }

  protected run(i: number): Observable<boolean> {
    return new Observable<boolean>(observer => {
      const detailSection = this.detailSections[ i ];
      if (detailSection) {
        detailSection.save(observer);
      } else {
        observer.next(true);
        observer.complete();
      }
    });
  }
}

@Component({
  selector: 'lm-detail-sections',
  standalone: false,
  templateUrl: './lm-detail-sections.component.html',
  styleUrls: [ 'lm-detail-sections.component.scss' ]
  })
export class LMDetailSectionsComponent implements AfterViewChecked, OnChanges, OnDestroy, OnInit {
  @Input() detailSectionPanels: Array<LandmarkPanel>;
  @Input() public detailSectionIdPrefix: string;
  @Input() public detailSectionAutomationIdPrefix: string;

  @Input() set fromDataView(fromDataView: LMDataView) {
    this.m_fromDataView = fromDataView;
  }
  get fromDataView(): LMDataView {
    return this.m_fromDataView;
  }

  @Input() isCardListDetailSections = false;
  @Input() listState: LMListState = {};
  @Input() showSinglePanelTab = false;
  @Input() hasContextCardView = false;
  @Input() overlayContent = false;
  @Input() fromListUrlInfo: LMListUrlInfo;

  @ViewChildren('detailListComponent') detailListComponents: QueryList<LMDetailSectionBase>;
  @ViewChild('sohoTabsComponent') sohoTabsComponent: SohoTabsComponent;

  @Output() panelContentDisplayed = new EventEmitter<LMContentDisplayedEvent>();
  @Output() sendContextMessageEvent = new EventEmitter<any>();
  @Output() contentModified = new EventEmitter<LMListFieldChangedEvent>();
  @Output() panelDataUpdated = new EventEmitter<LMListTransactionEvent>();
  @Output() uiLinkEvent = new EventEmitter<any>();
  @Output() cubeDetailSectionPVPeriodEvent = new EventEmitter<LMCubeDetailSectionPVPeriodEvent>();
  @Output() drillAroundEvent = new EventEmitter<LMDrillAroundEvent>();
  /**
   * Using null to determine if fromDataView is set or not.
   * Each detail section component needs to handle a null `fromDataView`
   * by disabling itself and removing its current data.
   */
  private m_fromDataView: LMDataView | null = null;
  private m_componentDestroyed = false;
  private activateSinglePanel = false;
  private runHandleResize = false;

  public visiblePanels: Array<LandmarkPanel>;
  public initialBuildComplete = false;
  public testVersion1: boolean;
  public detailSectionAttributes: SohoAttributesArray = [];
  public uiLoadingIndicatorTimeout = 0;
  public updateVisibilityTimestamp: number;

  public lazyDashboardConditions: boolean;
  private fFromDataViewChanged = false;
  public resolvedFromDataView: LMDataView;

  public readonly tabIdSuffix = AppConstants.ELEMENT_ID_SUFFIX_DETAIL_SECTION_TAB;

  constructor(
    private lmBusyIndicatorService: LMBusyIndicatorService,
    private lmContextService: LMContextService,
    private lmOverlayService: LMOverlayService,
    private lmConditionUtilService: LMConditionUtilService,
    private lmFormRequestService: LMFormRequestService,
    private lmUrlUtilService: LMUrlUtilService,
    private lmLoggerService: LMLoggerService,
    private lmErrorUtilService: LMErrorUtilService,
  ) {
    this.uiLoadingIndicatorTimeout = this.lmContextService.uiLoadingIndicatorTimeout;
    this.lazyDashboardConditions = this.lmContextService.lazyConditionsOnNestedDashBoardDetailSections();
  }

  ngOnInit() {
    this.testVersion1 = this.lmContextService.testVersion() === AppConstants.TESTVERSION_V1;
    this.detailSectionAttributes.push({ name: 'data-automation-id', value: 'lm-' + this.detailSectionAutomationIdPrefix + '-tabs' });
    if (!this.testVersion1) {
      this.detailSectionAttributes.push({ name: 'id', value: this.detailSectionIdPrefix + AppConstants.ELEMENT_ID_SUFFIX_DETAIL_SECTION_TABSET });
    }

    this.build();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.fromDataView) {
      if (this.initialBuildComplete) {
        if (!this.lazyDashboardConditions) {
          // if lazyDashboardConditions config is off just set the resolveFromDataview
          // here directly to avoid a full re-layout of the dashboard panels.
          this.resolvedFromDataView = this.fromDataView;
        } else {
          this.fFromDataViewChanged = true;
        }
      }
      this.build();
    }
  }

  ngAfterViewChecked() {
    if (this.activateSinglePanel) {
      // todo uncomment this and this.getVisiblePanelIndex() for april CU
      // const panelIndex = this.getVisiblePanelIndex();

      let panelIndex = 0;
      for (let i = 0; i < this.detailSectionPanels.length; i++) {
        const panel: LandmarkPanel = this.detailSectionPanels[i];

        // reset panels to hidden so that processDasbhboardVisibilityConditions
        // will run w/o the dashboard panes being displayed before the
        // get lazy conditions fields is executed.
        if (this.lazyDashboardConditions && panel.type === 'dashboard') {
          panel.needsReload = true;
        }

        // Need to find the panel that is not hidden and not inValid
        if (!panel.hidden && panel.isValid) {
          panelIndex = i;
        }
      }
      this.activateSinglePanel = false;
      setTimeout(() => {
        this.activatePanel(panelIndex);
      }, 1);
      return;
    }

    if (this.fFromDataViewChanged) {
      // need to update panelconditions when using the fromDataView changes
      // and we are using multiple tabs. Single panel is taken care of above
      // where we check this.activateSinglePanel.
      this.fFromDataViewChanged = false;

      if (this.useSohoTabsComponent) {
        this.processDashboardVisibilityConditions();
      }
    }

    if (this.runHandleResize) {
      if (this.useSohoTabsComponent) {
        setTimeout(() => this.sohoTabsComponent.handleResize());
      }
      this.runHandleResize = false;
    }
  }

  // todo uncomment this and use it above in ngAfterViewChecked,
  //  consider placing this into a service where it can be tested
  // private getVisiblePanelIndex(): number {
  //   const panelIndex = this.detailSectionPanels.findIndex(panel => !panel.hidden);
  //   return panelIndex > -1 ? panelIndex : 0;
  // }

  ngOnDestroy() {
    this.setComponentDestroyed();
  }

  private build() {
    if (this.hasPanels && this.fromDataView) {
      this.updateTabVisibility();
      this.checkSinglePanel();
      this.updateDetailSections();

      if (!this.initialBuildComplete) {
        this.initialBuildComplete = true;
      } else {
        this.runHandleResize = true;
      }
    }
  }

  onTabActivated(event: SohoTabsEvent) {
    setTimeout(() => {
      const panelIndex = event.tab ? this.getTabIndex(event.tab) : this.activePanelIndex;
      this.activatePanel(panelIndex, true);
    }, 1);
  }

  onPanelDataUpdated(event: LMListTransactionEvent) {
    this.panelDataUpdated.emit(event);
  }

  onDrillAroundEvent(event: LMDrillAroundEvent) {
    if (this.visiblePanels) {
      const selectedPanel = this.visiblePanels[this.activePanelIndex];
      if (selectedPanel) {
        const detail: LMDrillAroundDetail = {
          type: 'panel',
          panel: selectedPanel.title,
          panelIndex: this.activePanelIndex.toString(10),
          drillActions: undefined
        };
        event.chain.unshift(detail);
      }
    }

    this.drillAroundEvent.emit(event);
  }

  onDashboardContentDisplayed(event) {
    this.panelContentDisplayed.emit(event);
  }

  onSendContextMessageEvent(event: any) {
    this.sendContextMessageEvent.emit(event);
  }

  onContentModified(event: LMListFieldChangedEvent) {
    this.contentModified.emit(event);
  }

  onDetailSectionDisplayed(event: any) {
    // this.lmBusyIndicatorService.hideBusyIndicator(this.sohoBusyIndicator);
  }

  onUILinkEvent(event: any) {
    this.uiLinkEvent.emit(event);
  }

  onCubeDetailSectionPVPeriodEvent(event: LMCubeDetailSectionPVPeriodEvent) {
    this.cubeDetailSectionPVPeriodEvent.emit(event);
  }

  trackById(index: number, panel: LandmarkPanel): string {
    return panel.id;
  }

  get hasPanels(): boolean {
    if (this.detailSectionPanels) {
      return this.detailSectionPanels.some(panel => !panel.hidden && panel.isValid);
    }

    return false;
  }

  get useSohoTabsComponent() {
    if (this.showSinglePanelTab) {
      return true;
    }

    this.visiblePanels = this.detailSectionPanels.filter (panel => !panel.hidden && panel.isValid);
    return this.visiblePanels.length > 1;
  }

  public isModified(): boolean {
    return this.detailListComponents.toArray().some(detailSectionComponent =>
      detailSectionComponent.isModified());
  }

  public getDetailSectionList(listId): LMListComponent {
    let returnList;
    this.detailListComponents.toArray().filter((detailSection: LMDetailSectionBase) => detailSection.type === 'list')
      .filter((detailSectionList: LMDetailSectionListComponent) => detailSectionList.listSpec.resourceId === listId)
      .map((detailSectionList: LMDetailSectionListComponent) => returnList = detailSectionList.lmListComponent);
    return returnList;
  }

  private activatePanel(panelIndex: number, userInitiated = false) {
    // only close the flyout if it's a user initiated click. activatePanel can also be called from a refresh on 
    // a parent list, when only one detail section is visible.
    if (userInitiated && this.lmContextService.useListFormOverlay() && this.lmOverlayService.hasOpenOverlay && !this.overlayContent) {
      this.lmOverlayService.close();
    }

    if (this.detailSectionPanels[panelIndex].hidden) {
      // safety - sometimes the timeouts allow for index of 0 to be activated event when it's hidden.
      // this is most notably an issue with soho-tabs where init'ing the tabs.js component causes an
      // activated event of tab '0' before any user input.
      return;
    }

    this.listState.selectedTabIndex = panelIndex;
    let doUpdateDashboardPanelTabVisibility = false;

    const detailSectionPanel = this.detailSectionPanels[panelIndex];
    if (!detailSectionPanel.activated) {
      if (this.lazyDashboardConditions && detailSectionPanel.type === 'dashboard') {
        doUpdateDashboardPanelTabVisibility = true;
      }

      detailSectionPanel.activated = true;

    } else if (detailSectionPanel.needsReload) {
      if (this.lazyDashboardConditions && detailSectionPanel.type === 'dashboard') {
        doUpdateDashboardPanelTabVisibility = true;
      }

      detailSectionPanel.needsReload = false;
    }

    if (doUpdateDashboardPanelTabVisibility) {
      this.updateDashboardPanelVisibility(detailSectionPanel).subscribe((result: boolean) => {
        this.resolvedFromDataView = this.fromDataView;
        const detailDashboard = this.detailListComponents.toArray()[this.activePanelIndex] as LMDetailSectionDashBoardComponent;
        if (detailDashboard) {
          detailDashboard.refreshDashboard();
        }
      });
    } else {
      if (detailSectionPanel.type === 'dashboard') {
        this.resolvedFromDataView = this.fromDataView;
        this.updateVisibilityTimestamp = Date.now();
        const detailDashboard = this.detailListComponents.toArray()[this.activePanelIndex] as LMDetailSectionDashBoardComponent;
        if (detailDashboard) {
          detailDashboard.refreshDashboard();
        }
      }
    }
  }

  private getTabIndex(tab: HTMLElement): number {
    return Number(tab.dataset.index);
  }

  private get activePanelIndex() {
    if (!this.useSohoTabsComponent) {
      return 0;
    }

    return this.listState.selectedTabIndex;
  }

  private updateDetailSections(): void {
    if (!this.hasPanels) {
      return; // safety
    }

    const index = this.activePanelIndex; // returns 0 if not useSohoTabsComponent === false

    // if (index === -1) {
    //   this.markTabsForReload(index);
    //   return;
    // }

    // this.detailSectionPanels[ index ].fromDataView = this.fromDataView;
    if (this.useSohoTabsComponent) {
      this.markTabsForReload(index);
    }
  }

  private checkSinglePanel() {
    if (!this.fromDataView || !this.hasPanels) {
      return;
    }

    if (!this.useSohoTabsComponent) {
      this.activateSinglePanel = true;
    }
  }

  private markTabsForReload(selectedIndex: any) {
    this.detailSectionPanels
      .filter((panel, index) => index !== selectedIndex)
      .forEach(panel => panel.needsReload = panel.activated);
  }

  private updateTabVisibility() {
    if (!this.hasPanels || !this.fromDataView) {
      return;
    }

    // DPB List does this work
    // this.detailSectionPanels.forEach((panel: LandmarkPanel) =>
    //   panel.hidden = (panel.visibility && this.fromDataView) ? !this.lmConditionUtilService.isValid(panel.visibility, this.fromDataView) : false);

    if (this.listState.selectedTabIndex === undefined) {
      this.listState.selectedTabIndex = 0;
    }

    if (this.listState.selectedTabIndex > this.detailSectionPanels.length - 1) {
      this.listState.selectedTabIndex = 0; // safety
    }

    const currentPanel = this.detailSectionPanels[this.listState.selectedTabIndex];
    if (currentPanel.hidden || !currentPanel.isValid ) {
      const nearestPanel = this.findNearestVisibleValidPanel(this.listState.selectedTabIndex);
      if (nearestPanel) {
        if (!this.initialBuildComplete) {
          // ------------------------------------------------------------------------------------
          // if initial build then use template variables to pass in the selection. Otherwise
          // we get a scrollIntoView that causes the screen to get hosed up when detail
          // sections are used with a card list in mobile view. See LMCLIENT-22363 for details
          // ------------------------------------------------------------------------------------
          this.detailSectionPanels.forEach(panel => panel.selected = false);
          nearestPanel.selected = true;
        } else {
          if (this.useSohoTabsComponent) {
            // todo ppatton claudenbach -  concern about setting activated = false. This may
            // causes slight performance degradation due to the datagrid being deconstructed.
            // See LMCLIENT-31795 for details and TCLMRK-4392 for automated testing.
            nearestPanel.activated = false;

            setTimeout(() => {
              //----------------------------------------------------------------------
              // use case:
              // 1. Detail sections with valid when and visible when at the beginning
              // 2. all detail sections has valid false
              // 3. Card View List with detail section (Manufacturer.CardListDetailSectionsSearchField)
              // 4. Composite Form with multiple panels, the resource in panel is a card view list with detail section
              //    (Manufacturer.CompositeFormWithMultiplePanels)
              // 5. no.3 and 4 but let the user select a different tab other than the default one
              //    and make sure that the focus stays on that tab even if we select a different from row from cadrview list
              //-----------------------------------------------------
              this.sohoTabsComponent.select('#' + nearestPanel.id, true);
            });
          }
        }
      }
    }
  }

  findNearestVisibleValidPanel(selectedIndex: number): LandmarkPanel {

    for (let i = selectedIndex; i > -1; i--) {
      const panel = this.detailSectionPanels[i];
      if (!panel.hidden && panel.isValid) {
        return panel;
      }
    }

    for (let i = selectedIndex, length = this.detailSectionPanels.length; i < length; i++) {
      const panel = this.detailSectionPanels[i];
      if (!panel.hidden && panel.isValid) {
        return panel;
      }
    }

    return null;
  }

  refresh(request?: LMRefreshRequest): Observable<boolean> {
    return new Observable<any>(observer => {
      const detailListComponentsArray = this.detailListComponents.toArray();
      const detailSection = detailListComponentsArray[ this.activePanelIndex ];

      if (detailSection) {
        detailSection.refresh(request).subscribe(() => {
          if (this.useSohoTabsComponent) {
            this.markTabsForReload(this.activePanelIndex);
          }
          observer.next(true);
          observer.complete();
        });
      } else {
        observer.next(true);
        observer.complete();
      }
    });
  }

  runSaveAction(): Observable<boolean> {
    const detailListComponentsArray = this.detailListComponents.toArray();
    const modifiedDetailSections = detailListComponentsArray.filter(detailSectionComponent => detailSectionComponent.isModified());

    return new Observable<boolean>(observer => {
      this.lmBusyIndicatorService.showBusyIndicator();
      this.saveDetailSections(modifiedDetailSections).subscribe((continueOn: boolean) => {
        this.lmBusyIndicatorService.hideBusyIndicator();
        observer.next(continueOn);
        observer.complete();
      },
      (err => {
        // let errorPanel: LMDetailSectionBase = err;
        // if (errorPanel) {
        //   this.sohoTabsComponent.select('#' + errorPanel.panelId);
        observer.next(false);
        observer.complete();
      }
      ));

    });
  }

  saveDetailSections(detailSections: LMDetailSectionBase[]): Observable<boolean> {
    const saveWorker: LMDetailSectionsSaveWorker = new LMDetailSectionsSaveWorker(detailSections);
    return saveWorker.start();
  }

  saveDetailSectionsState(): void {
    this.detailListComponents.toArray().forEach(detailListComponent => detailListComponent.saveDetailSectionState());
  }

  clearDetailSectionState(): void {
    this.detailListComponents.toArray().forEach(detailListComponent => detailListComponent.clearDetailSectionState());
  }

  public get componentDestroyed(): boolean {
    return this.m_componentDestroyed;
  }

  protected setComponentDestroyed() {
    this.m_componentDestroyed = true;
  }

  // used for when fromDataView changes on a multi tab detail section with
  // dashboard detail sections. See ngAfterViewChecked for where this is called.
  private processDashboardVisibilityConditions() {
    if (this.initialBuildComplete) {
      const detailSectionPanel = this.detailSectionPanels[this.listState.selectedTabIndex];
      if (this.lazyDashboardConditions && detailSectionPanel.type === 'dashboard') {
        // Gate: hold back fromDataView from dashboard children until async visibility
        // update completes
        this.updateDashboardPanelVisibility(detailSectionPanel).subscribe(() => {
          this.resolvedFromDataView = this.fromDataView;
        });
      } else {
        // No gating needed — just set the resolvedFromDataView
        if (detailSectionPanel.type === 'dashboard') {
          this.resolvedFromDataView = this.fromDataView;
        }
      }
    }
  }

  private updateDashboardPanelVisibility(detailSectionPanel: LandmarkPanel): Observable<boolean> {
    if (!this.lazyDashboardConditions) {
      return of(true);
    }

    const requestFields = this.getFieldsFromConditions(detailSectionPanel.dashBoardPanes, this.fromDataView);
    if (!requestFields || requestFields.length === 0) {
      // conditions still need processing because a previously activated tab may have
      // already fetched the lazy-condition fields into the dataView, so there are no
      // new fields to request but the conditions still need to be evaluated.
      this.processDashboardPanelConditions(detailSectionPanel.dashBoardPanes);
      return of(true);
    }
    if (this.lmLoggerService.cskLog > 0) {
      // logger service doesn't handle a message and an optional array of objects.
      // And turning it on seems to crash my devtools window in chrome. firefox seems find though.
      // this.lmLoggerService.log('abc'); // [ requestFields ]);
      console.log('dashboard condition fields request', [ requestFields ]);
    }

    const requestUrlTemplate = urltemplate.parse(this.lmUrlUtilService.buildFormRequestUrl(this.fromListUrlInfo));
    const requestUrl = requestUrlTemplate.expand({ action: 'GETVALUE', resourceId: this.fromDataView.resourceId });

    return new Observable(observer => {
      this.lmFormRequestService.getFieldValues(requestUrl, requestFields).pipe(
        first()
      ).subscribe({
        next: response => {
          if (response.status === 'Completed') {
            if (this.lmLoggerService.cskLog > 0) {
              // logger service doesn't handle a message and an optional array of objects.
              // And turning it on seems to crash my devtools window in chrome. firefox seems find though.
              // this.lmLoggerService.log('### dashboard conditions fields');
              // this.lmLoggerService.log([ response.fields ]);
              console.log('dashboard condition fields:', [ response.fields ]);
            }

            this.addFieldsToFromDataView(response.fields);
            this.processDashboardPanelConditions(detailSectionPanel.dashBoardPanes);
            observer.next(true);
            observer.complete();

          } else if (response.status === 'Exception') {
            this.lmErrorUtilService.handleError(response.message, response.details, response.status);
            observer.next(false);
            observer.complete();
          }
        },
        error: error => {
          this.lmLoggerService.error(error);
          this.lmErrorUtilService.handleError(error.message || error, null, 'Exception');
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  private getFieldsFromConditions(dashboardPanes: LandmarkPanel[], dataView: LMDataView): Array<string> {
    const fieldList: Array<string> = [];
    for (let i = 0, len = dashboardPanes.length; i < len; i++) {
      const panel: LandmarkPanel = dashboardPanes[i];

      if (panel?.visibility?.length > 0 && panel.visibility[0] !== undefined) {
        for (let j = 0, len1 = panel.visibility.length; j < len1; j++) {
          const condition: LMCondition = panel.visibility[j];
          // exclude fields that are already in the dataview. Condition.type === value means a visible when (true) or
          // visible when (false), and also should not be included in the list
          if (condition.type !== 'value' && !this.fieldIsInFromDataView(condition.fieldId)) {
            fieldList.push(condition.fieldId);
          }
        }
      }

      if (panel?.validity?.length > 0 && panel.validity[0] !== undefined) {
        for (let j = 0, len1 = panel.validity.length; j < len1; j++) {
          const condition: LMCondition = panel.validity[j];
          // exclude fields that are already in the dataview. Condition.type === value means a visible when (true) or
          // visible when (false), and also should not be included in the list
          if (condition.type !== 'value' && !this.fieldIsInFromDataView(condition.fieldId)) {
            fieldList.push(condition.fieldId);
          }
        }
      }
    }

    return fieldList;
  }

  private processDashboardPanelConditions(dashboardPanes: LandmarkPanel[]) {
    this._updateDashboardPanels(dashboardPanes);
    // needed so the dashboard will refresh/re-layout it's dashboard panes
    this.updateVisibilityTimestamp = Date.now();
  }

  private _updateDashboardPanels(dashboardPanes: LandmarkPanel[]): void {
    for (let i = 0, len = dashboardPanes.length; i < len; i++) {
      const dashboardPane: LandmarkPanel = dashboardPanes[i];
      if (!dashboardPane) {
        continue;
      }

      // need to check for panel because the dashboard detail section implementation
      // does not support all types yet, such as URLPanels
      this.updateDashboardPanel(dashboardPane);
    }
  }

  private updateDashboardPanel(dashboardPane: LandmarkPanel) {
    if (dashboardPane?.visibility?.length > 0 && dashboardPane.visibility[0] !== undefined) {
      dashboardPane.hidden = !this.lmConditionUtilService.isValid(dashboardPane.visibility, this.fromDataView);
    }

    if (dashboardPane.validity?.length > 0 && dashboardPane.validity[0] !== undefined) {
      dashboardPane.isValid = this.lmConditionUtilService.isValid(dashboardPane.validity, this.fromDataView);
    }

    dashboardPane.isVisible = dashboardPane.isValid && !dashboardPane.hidden;
  }

  private addFieldsToFromDataView(fields: LMFields) {
    if (!this.fromDataView) {
      return;
    }
    Object.keys(fields).forEach(key => {
      const newField = fields[key];
      if (!this.fieldIsInFromDataView(key)) {
        // don't add field if it's already in the dataView
        this.fromDataView.fields[key] = newField;
      }
    });
  }

  private fieldIsInFromDataView(fieldId: string): boolean {
    if (this.fromDataView && this.fromDataView.fields && this.fromDataView.fields[fieldId]) {
      return true;
    }
    return false;
  }
}
